import {gql, mutate, query, watchQuery} from '@/services/apollo'
import {getToken} from '@/keycloak'

const FRAGMENTS = {
  CASINO: gql`
    fragment Casino on Casino {
      id,
      name,
      casinoId,
      integrationId,
      adapter {
        adapterId
        config {
          key
          value
        }
      },
      jurisdiction {
        id
        key
      }
      gameRoundValidityPeriod
      maxRTP
      options {
        showDevelopStartPage,
        sendEventMessages
      }
      integrationOptionIds {
        id
        value
      }
    }
  `,
  OPEN_GAME_ROUND: gql`
    fragment OpenGameRound on OpenGameRound {
      id
      playerId
      gameName
      creationDate,
      credit,
      debit,
      totalWin
    }
  `
}


const GET_CASINO = gql`
  query CasinoQuery($input: CasinoInput!) {
    casino(input: $input) {
      ...Casino
      integrationOptions {
        id,
        name,
        description
      }
    }
  }

  ${FRAGMENTS.CASINO}
`

const GET_CASINOS = gql`
  query CasinoPage($input: CasinoPageInput!) {
    casinoPage(input: $input) {
      casino {
        id
        name
        casinoId
      }
      totalCount(input: $input)
    }
  }
`

const CREATE_CASINO = gql`
  mutation CreateCasino($input: CreateCasinoInput!) {
    createCasino(input: $input) {
      ...Casino
    }
  }

  ${FRAGMENTS.CASINO}
`

const EDIT_CASINO = gql`
  mutation EditCasino($input: EditCasinoInput!) {
    editCasino(input: $input) {
      ...Casino
    }
  }

  ${FRAGMENTS.CASINO}
`

const DELETE_CASINO = gql`
  mutation DeleteCasino($input: DeleteCasinoInput!) {
    deleteCasino(input: $input)
  }
`

const OPEN_GAME_ROUNDS = gql`
  query openGameRounds($input: OpenGameRoundsInput!) {
    openGameRounds(input: $input) {
      ...OpenGameRound
    }
  }

  ${FRAGMENTS.OPEN_GAME_ROUND}
`

const OPEN_GAME_ROUND = gql`
  query openGameRound($input: OpenGameRoundInput!) {
    openGameRound(input: $input) {
      ...OpenGameRound
    }
  }

  ${FRAGMENTS.OPEN_GAME_ROUND}
`

const REFUND_OPEN_GAME_ROUND = gql`
  mutation refundOpenGameRound($input: RefundOpenGameRoundInput!) {
    refundOpenGameRound(input: $input)
  }
`

export default {
  getCasinos (variables) {
    return watchQuery({ query: GET_CASINOS, variables: { input: { integrationId: variables.integrationId, limit: 0, offset: 0 } } })
  },
  getCasino (variables) {
    return watchQuery({ query: GET_CASINO, variables })
  },
  createCasino (input) {
    return mutate({
      mutation: CREATE_CASINO,
      variables: { input }
    })
  },
  editCasino (input) {
    return mutate({
      mutation: EDIT_CASINO,
      variables: { input }
    })
  },
  deleteCasino (input) {
    return mutate({
      mutation: DELETE_CASINO,
      variables: { input }
    })
  },
  async getCasinoConfiguration (casinoId) {
    const response = await fetch('/casino/configuration/validate', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + await getToken()
      },
      body: JSON.stringify({
        casinoId: casinoId,
      })
    })

    if (response.status !== 200)
      throw response.status

    return await response.json()
  },
  async pushCasinoConfiguration (casinoId) {
    const response = await fetch('/casino/configuration/push', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + await getToken()
      },
      body: JSON.stringify({
        casinoId: casinoId,
      })
    })

    if (response.status !== 200) {
      throw await response.text()
    }

    return response
  },
  getCasinoIntegrationOptionPage (variables) {
    return watchQuery({
      query: gql`
        query casinoIntegrationOptionPage($input: CasinoIntegrationOptionPageInput!) {
          casinoIntegrationOptionPage {
            casinoIntegrationOptions(input: $input) {
              id,
              name,
              dataType,
              description
            }
            totalCount
          }
        }
      `,
      variables
    })
  },
  createCasinoIntegrationOption (input) {
    return mutate({
      mutation: gql`
        mutation createCasinoIntegrationOption($input: CreateCasinoIntegrationOptionInput!) {
          createCasinoIntegrationOption(input: $input) {
            id,
            name,
            dataType,
            description
          }
        }
      `,
      variables: { input }
    })
  },
  getCasinoIntegrationOption (variables) {
    return watchQuery({
      query: gql`
        query casinoIntegrationOption($input: CasinoIntegrationOptionInput!) {
          casinoIntegrationOption(input: $input) {
              id,
              name,
              dataType,
              description
          }
        }
      `,
      variables
    })
  },
  editCasinoIntegrationOption (input) {
    return mutate({
      mutation: gql`
        mutation editCasinoIntegrationOption($input: EditCasinoIntegrationOptionInput!) {
          editCasinoIntegrationOption(input: $input) {
            id,
            name,
            description
          }
        }
      `,
      variables: { input }
    })
  },
  deleteCasinoIntegrationOption (input) {
    return mutate({
      mutation: gql`
        mutation deleteCasinoIntegrationOption($input: DeleteCasinoIntegrationOptionInput!) {
          deleteCasinoIntegrationOption(input: $input)
        }
      `,
      variables: { input }
    })
  },
  getOpenGameRounds (input) {
    return query({
      query: OPEN_GAME_ROUNDS,
      variables: { input }
    })
  },
  getOpenGameRound (input) {
    return watchQuery({
      query: OPEN_GAME_ROUND,
      variables: { input }
    })
  },
  refundOpenGameRound (input) {
    return mutate({
      mutation: REFUND_OPEN_GAME_ROUND,
      variables: { input }
    })
  }
}
